import { Component, Fragment, h, Prop, State } from '@stencil/core';
import { MenuItem, Sources } from './interfaces';

@Component({
  tag: 'salla-menu',
  styleUrl: 'salla-menu.scss',
})

export class SallaMenu {

  /**
   * The source of the menu, specifying whether it is a header or footer menu.
   */
  @Prop() source: Sources = "header"

  /**
   * The source value, a stringified JSON representation of the menu content.
   */
  @Prop() sourceValue?: string;

  /**
   * Boolean indicating whether the menu is a top navigation menu.
   */
  @Prop() topnav: boolean;

  /**
   * Boolean indicating whether to use React Link elements for menu links.
   */
  @Prop() useReactLink: boolean = false;

  /**
   * Limiting the number of menu items
   */
  @Prop() limit: number;

  @State() menus: MenuItem[] = [];

  private displayAllText: ''

  async componentWillLoad() {
    return salla.onReady(() => {
      this.displayAllText = salla.lang.get('blocks.home.display_all');
      if (this.sourceValue && this.source === 'json') {
        this.menus = JSON.parse(this.sourceValue) || []
      } else {
        return this.getMenuItems();
      }

    });
  }


  private getMenuItems() {

    /**
      * Avoid saving the menu to localStorage (default) when in the development environment
      * or when modifying the theme in the dashboard
      */
    const isPreview = salla.config.isDebug() || salla.helpers.isPreview()
    const cacheKey = `${this.source}_menus_${salla.lang.locale}`
    const cachedMenus = salla.storage.getWithTTL(cacheKey, [])

    if (cachedMenus.length > 0 && !isPreview) {
      this.menus = cachedMenus
      return;
    }

    return salla.api.component.getMenus(this.source).then(({ data }) => {
      this.menus = data;
      !isPreview && salla.storage.setWithTTL(cacheKey, this.menus)
    }).catch((error) => {
      salla.logger.error('salla-menu::Error fetching menus', error)
    });
  }

  private hasChildren(menu: MenuItem) {
    return menu?.children?.length > 0;
  }


  private hasProducts(menu: MenuItem) {
    return menu?.products?.length > 0;
  }


  /**
  * Get the classes for desktop menu
  * @param {Object} menu
  * @param {Boolean} isRootMenu
  * @returns {String}
  */
  private getDesktopClasses(menu: MenuItem, isRootMenu: boolean) {
    return `!hidden lg:!block ${isRootMenu ? 'root-level lg:!inline-block' : 'relative'} ${menu.products ? ' mega-menu' : ''}
    ${this.hasChildren(menu) ? 'has-children' : ''}`
  }

  private getAttributes(attrs: string) {
    return attrs.trim().split(' ').reduce((result, attr) => {
      let [key, value] = attr.split('=');
      result[key] = value.replace(/"/g, '');
      return result;
    }, {});
  }

  /**
     * Get the mobile menu
     * @param {Object} menu
     * @param {String} displayAllText
     * @returns {String}
     */
  private getMobileMenu(menu: MenuItem, displayAllText: string) {
    const menuImage = menu.image ? <img src={menu.image} class="rounded-full" width="48" height="48" alt={menu.title} /> : null;

    return (
      <li class="lg:hidden text-sm font-bold" {...this.getAttributes(menu.attrs)}>
        {!this.hasChildren(menu) ?
          <a href={menu.url} aria-label={menu.title || 'category'} class={`text-gray-500 ${menu.image ? '!py-3' : ''}`} {...this.getAttributes(menu.link_attrs)}>
            {menuImage}
            <span>{menu.title || ''}</span>
          </a>
          :
          <Fragment>
            <span class={menu.image ? '!py-3' : ''}>
              {menuImage}
              {menu.title}
            </span>
            <ul>
              <li class="text-sm font-bold">
                <a href={menu.url} class="text-gray-500">{displayAllText}</a>
              </li>
              {menu.children.map((subMenu) => this.getMobileMenu(subMenu, displayAllText))}
            </ul>
          </Fragment>
        }
      </li>
    )
  }


  /**
     * Get the desktop menu
     * @param {Object} menu
     * @param {Boolean} isRootMenu
     * @returns {String}
     */
  private getDesktopMenu(menu: MenuItem, isRootMenu: boolean) {

    return <li class={this.getDesktopClasses(menu, isRootMenu)} {...this.getAttributes(menu.attrs)}>
      <a href={menu.url} aria-label={menu.title || 'category'} {...this.getAttributes(menu.link_attrs)}>
        <span>{menu.title}</span>
      </a>
      {this.hasChildren(menu) ?
        <div class={`sub-menu shadow-default ${this.hasProducts(menu) ? 'w-full left-0 flex' : 'w-56'}`}>
          <ul class={this.hasProducts(menu) ? 'w-56 shrink-0 m-8 rtl:ml-0 ltr:mr-0' : ''}>
            {menu.children.map((subMenu) => this.getDesktopMenu(subMenu, false))}
          </ul>
          {this.hasProducts(menu) ?
            <salla-products-list
              source="selected"
              shadow-on-hover
              source-value={`[${menu.products}]`}
            /> : null}
        </div> : null}
    </li>
  }


  /**
     * Get the footer menu
     * @param {Array} menus
     */
  private getFooterMenu(menus: Partial<MenuItem>[]) {
    const Menuitems = (this.topnav || this.limit) ? menus.slice(0, this.limit || 3) : menus

    return <div>
      <div class={{
        "s-menu-footer-list": !this.topnav,
        "s-menu-topnav-list": this.topnav
      }}
      >
        {Menuitems.map((menu: Partial<MenuItem>) => {
          return <a key={menu.id} href={menu.url} target={menu.target} class={{
            "s-menu-footer-item": !this.topnav,
            "s-menu-topnav-item topnav-link-item right-side": this.topnav
          }}>{menu.title}</a>
        })}
      </div>
    </div>
  }

  /**
* Get the menus
* @param {Array} menus
* @returns {String}
*/
  private getHeaderMenu(menus: MenuItem[]) {
    return menus.map((menu: MenuItem) =>
      [
        this.getMobileMenu(menu, this.displayAllText),
        this.getDesktopMenu(menu, true)
      ]
    )
  }



  render() {
    return this.source === "footer" ?
      this.getFooterMenu(this.menus)
      :
      [
        <nav key="main-nav" id="mobile-menu" class="mobile-menu">
          <ul class="main-menu">
            {this.getHeaderMenu(this.menus)}
          </ul>
          <button class="btn--close close-mobile-menu sicon-cancel lg:hidden" />
        </nav>,
        <button key="close-btn" class="btn--close-sm close-mobile-menu sicon-cancel hidden" />
      ]
  }
}
